import {Action, Module, Mutation, VuexModule} from "vuex-module-decorators";
import AccountDataAES from "@/util/accountDataAES";
import SessionAdapter from "@/adapters/session";
import UploadAdapter from "@/adapters/upload";
import Upload from "@/models/Upload";
import Letterbox from "@/models/Letterbox";
import PersistentLogin from "@/util/persistentLogin";
import LoginInfo from "@/util/LoginInfo";
import User from "@/models/User";
import Base64Encoder from "@/util/base64encoder";
import Vue from "vue";
import BillingEvent from "@/models/BillingEvent";
import AdminAdapter from "@/adapters/admin";
import Invoice from "@/models/Invoice";

const sessionAdapter = new SessionAdapter();
const uploadAdapter = new UploadAdapter();
const adminAdapter = new AdminAdapter();

@Module
export default class AccountStore extends VuexModule {

    masterHash: string = "";
    masterKey: CryptoKey | null = null;
    username: string = "";
    accountData: User | null = null;
    myUploads: Upload[] = [];
    myUploadsLoading: boolean = false;
    myLetterboxes: Letterbox[] = [];
    myLetterboxesLoading: boolean = false;
    myBillingEvents: BillingEvent[] = [];
    myBillingEventsLoading: boolean = false;
    myInvoices: Invoice[] = [];
    myInvoicesLoading: boolean = false;

    get isLoggedIn() {
        return this.masterKey != null && this.masterHash !== "";
    }

    get loggedInName(): string {
        if (this.isLoggedIn && this.accountData) {
            return (this.accountData.firstName + " " + this.accountData.lastName).trim();
        } else {
            return "";
        }
    }

    get isAdmin() {
        return this.accountData ? this.accountData.role === "ADMIN" : false;
    }

    @Mutation
    setLoggedIn({username, hash, key, persistent}: {username: string, hash: string, key: CryptoKey, persistent: boolean}) {
        this.username = username;
        this.masterHash = hash;
        this.masterKey = key;
        void PersistentLogin.saveLogin(key, hash, username, persistent);
    }

    @Mutation
    restoreLogin({username, hash, key}: {username: string, hash: string, key: CryptoKey}) {
        this.username = username;
        this.masterHash = hash;
        this.masterKey = key;
    }

    @Mutation
    setAccountData(data: User) {
        this.accountData = data;
    }

    @Mutation
    setAccountUploads(uploads: Upload[]) {
        this.myUploads = uploads;
    }

    @Mutation
    setAccountUploadsLoading(state: boolean) {
        this.myUploadsLoading = state;
    }

    @Mutation
    setAccountLetterboxes(letterboxes: Letterbox[]) {
        this.myLetterboxes = letterboxes;
    }

    @Mutation
    setAccountLetterboxesLoading(state: boolean) {
        this.myLetterboxesLoading = state;
    }

    @Mutation
    locallyDeleteUpload(id: string) {
        const idx: number = this.myUploads.findIndex((upload: Upload) => {
            return upload.id === id;
        });
        this.myUploads.splice(idx, 1);
    }

    @Mutation
    locallyDeleteLetterbox(id: string) {
        const idx: number = this.myLetterboxes.findIndex((letterbox: Letterbox) => {
            return letterbox.id === id;
        });
        this.myLetterboxes.splice(idx, 1);
    }

    @Mutation
    updateUpload(newUpload: Upload) {
        this.myUploads = this.myUploads.map((upload: Upload) => {
            if (upload.id === newUpload.id) {
                return newUpload;
            } else {
                return upload;
            }
        });
    }

    @Mutation
    setBillingEvents(events: BillingEvent[]) {
        this.myBillingEvents = events;
    }

    @Mutation
    setBillingEventsLoading(state: boolean) {
        this.myBillingEventsLoading = state;
    }

    @Mutation
    setInvoices(invoices: Invoice[]) {
        this.myInvoices = invoices;
    }

    @Mutation
    setInvoicesLoading(state: boolean) {
        this.myInvoicesLoading = state;
    }

    @Mutation
    logout() {
        this.username = "";
        this.masterHash = "";
        this.masterKey = null;
        this.accountData = null;
        this.myUploads = [];
        this.myLetterboxes = [];
        PersistentLogin.deleteInformation();
    }

    @Action({ rawError: true })
    async login({username, password, persistent}: {username: string, password: string, persistent: boolean}) {

        Vue.$log.debug("Store login action called", arguments);
        //Generate Keys and hashes:
        const tempAccountKey: CryptoKey | null = await AccountDataAES.generateMasterKeyFromUserPassword(username, password);
        if (!tempAccountKey) {
            throw new Error("Could not compute account key");
        }
        const tempMasterHash: string = await AccountDataAES.generateAuthenticationHashFromMasterKey(tempAccountKey, password);

        const data: User = await sessionAdapter.getAccountInfo(username, tempMasterHash);

        this.context.commit("setLoggedIn", {
            username,
            hash: tempMasterHash,
            key: tempAccountKey,
            persistent,
        });

        this.context.commit("setAccountData", data);
    }

    @Action({ rawError: true })
    async tryRestoreLogin() {
        try {
            const info: LoginInfo | null = await PersistentLogin.getLogin();
            if (info) {
                const data: User = await sessionAdapter.getAccountInfo(info.username, info.authHash);
                this.context.commit("setAccountData", data);
                this.context.commit("restoreLogin", {username: info.username, hash: info.authHash, key: info.masterKey});
            }
        } catch (e) {
            Vue.$log.error("Invalid Login Info found, clearing...");
            PersistentLogin.deleteInformation();
        }
    }

    @Action({ rawError: true })
    async fetchMyUploads() {
        try {
            this.context.commit('setAccountUploadsLoading', true);
            const uploads: Upload[] = await sessionAdapter.getAccountUploads();
            this.context.commit('setAccountUploads', uploads);
        } catch (e) {
            throw e;
        } finally {
            this.context.commit('setAccountUploadsLoading', false);
        }
    }

    @Action({ rawError: true })
    async fetchMyLetterboxes() {
        try {
            this.context.commit('setAccountLetterboxesLoading', true);
            const letterboxes: Letterbox[] = await sessionAdapter.getAccountLetterboxes();
            this.context.commit('setAccountLetterboxes', letterboxes);
        } catch (e) {
            throw e;
        } finally {
            this.context.commit('setAccountLetterboxesLoading', false);
        }
    }

    @Action({ rawError: true })
    async fetchMyBillingEvents() {
        try {
            this.context.commit('setBillingEventsLoading', true);
            const events: BillingEvent[] = await adminAdapter.getBillingEvents();
            this.context.commit('setBillingEvents', events);
        } catch (e) {
            throw e;
        } finally {
            this.context.commit('setBillingEventsLoading', false);
        }
    }

    @Action({ rawError: true })
    async fetchMyInvoices() {
        try {
            this.context.commit('setInvoicesLoading', true);
            const invoices: Invoice[] = await adminAdapter.getInvoices();
            this.context.commit('setInvoices', invoices);
        } catch (e) {
            throw e;
        } finally {
            this.context.commit('setInvoicesLoading', false);
        }
    }

    @Action({ rawError: true })
    async deleteUpload(id: string) {
        await sessionAdapter.deleteUpload(id);
        this.context.commit('locallyDeleteUpload', id);
    }

    @Action({ rawError: true })
    async deleteLetterbox(id: string) {
        await sessionAdapter.deleteLetterbox(id);
        this.context.commit('locallyDeleteLetterbox', id);
    }

    @Action({ rawError: true })
    async changePassword(newPassword: string) {
        if (!this.masterKey || !this.accountData) {
            throw new Error("No master key or account data available");
        }
        try {
            //Get old account key to rewrap:
            const oldAccountEncryption: AccountDataAES = await AccountDataAES.fromAccountData(this.masterKey, this.accountData);
            const oldKey: CryptoKey = oldAccountEncryption.getKey();

            //Generate Keys and master hashes:
            const newMasterKey: CryptoKey | null = await AccountDataAES.generateMasterKeyFromUserPassword(this.username, newPassword);
            if (!newMasterKey) {
                throw new Error("Could not compute master key");
            }
            const newAccountEncryption: AccountDataAES = new AccountDataAES(newMasterKey);
            const tempAuthHash: string = await AccountDataAES.generateAuthenticationHashFromMasterKey(newMasterKey, newPassword);

            //Generate new Values for the account:
            const newIV: Uint8Array = AccountDataAES.generateIV();
            const newAccountKeyValue: string = await newAccountEncryption.wrapKey(oldKey, newIV);

            //Set data object:
            const payload: User = Object.assign(new User(), this.accountData);
            payload.password = tempAuthHash;
            payload.accountKeyIv = Base64Encoder.bytesToString(newIV);
            payload.accountKey = newAccountKeyValue;

            //trigger remote action:
            await sessionAdapter.changePassword(payload);

            //Logout if succeeded:
            this.context.commit("logout");
        } catch (e) {
            console.error(e);
            throw e;
        }

    }

    @Action({ rawError: true })
    async changeUploadExpiration({id, newDate}: {id: string, newDate: Date}) {
        try {
            const newUpload = await sessionAdapter.changeExpiry(id, newDate);
            this.context.commit("updateUpload", newUpload);
        } catch (e) {
            Vue.$log.error(`Could not change upload expiry for upload ${id}`, e);
            throw e;
        }
    }
}
